import React, { useState, useRef, useEffect, useCallback } from 'react';

/**
 * SpiderTrack — a pantalla completa.
 *
 * El recorrido es uno solo: cargas la araña, se siguen sus patas por todo el
 * vídeo, se dibuja el trazo y se exporta.
 *
 * El seguimiento de verdad lo hacen SAM 3 y CoTracker3, que son PyTorch y no
 * caben en el navegador, así que corren en el servidor local de
 * spidertrack/servidor.py. Con la webcam, que es señal en vivo, el seguimiento
 * se hace aquí mismo por umbral de luminancia.
 */

const SERVIDOR = 'http://127.0.0.1:8910';
const ESCALA = [0, 2, 4, 7, 9];
const ANCHO_ANALISIS = 240;

const hexARgb = (hex) => {
  const n = parseInt(hex.replace('#', ''), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

const SpiderTrack = ({ onSalir }) => {
  const [fuente, setFuente] = useState(null);      // video | webcam | ejemplo | json
  const [nombre, setNombre] = useState('');
  const [fase, setFase] = useState('vacio');       // vacio | listo | analizando | siguiendo | directo
  const [aviso, setAviso] = useState('Carga el vídeo de una araña, o abre la webcam');
  const [servidorVivo, setServidorVivo] = useState(null);
  const [marcando, setMarcando] = useState(false);
  const [marcas, setMarcas] = useState([]);
  const [progreso, setProgreso] = useState({ pct: 0, mensaje: '', queda: null });
  const [tiempo, setTiempo] = useState({ t: 0, dur: 0 });

  const [enMarcha, setEnMarcha] = useState(true);
  const [plegado, setPlegado] = useState(false);

  const [pestana, setPestana] = useState('dibujo');
  const [color, setColor] = useState('#ffffff');
  // Se arranca con el vídeo a la vista: así se ve que el trazo va sobre las
  // patas y no sobre cualquier sitio. Bajando la opacidad a cero queda el trazo solo.
  const [opacidadVideo, setOpacidadVideo] = useState(0.45);
  const [eco, setEco] = useState(0.108);
  const [grosor, setGrosor] = useState(2.5);
  const [velocidad, setVelocidad] = useState(1);
  const [radios, setRadios] = useState(true);
  const [anillo, setAnillo] = useState(true);
  const [anclado, setAnclado] = useState(true);

  const [sonando, setSonando] = useState(false);
  const [volumen, setVolumen] = useState(0.35);

  const [lectura, setLectura] = useState({ movimiento: 0, radio: 0, puntos: 0, cuadro: 0, total: 0 });

  const videoRef = useRef(null);
  const analisisRef = useRef(null);
  const dibujoRef = useRef(null);
  const trazoRef = useRef(null);        // capa donde se acumula el trazo, aparte del vídeo
  const rafRef = useRef(null);
  const ultimoTickRef = useRef(0);
  const streamRef = useRef(null);
  const ficheroRef = useRef(null);
  const pistasRef = useRef(null);
  const crudoRef = useRef(null);
  const trabajoRef = useRef(null);
  const tramoRef = useRef(null);   // [desde, hasta] en segundos, lo que se analizó
  const idTrabajoRef = useRef(null);
  const cuadroRef = useRef(0);
  const encuadreRef = useRef(null);
  const previasRef = useRef(null);
  const trazosRef = useRef([]);
  const audioRef = useRef(null);
  const ganRef = useRef(null);
  const filtroRef = useRef(null);
  const ultimaNotaRef = useRef(0);

  const cfg = useRef({});
  useEffect(() => {
    cfg.current = { color, opacidadVideo, eco, grosor, velocidad, radios, anillo, sonando,
                    fase, marcando, enMarcha };
  }, [color, opacidadVideo, eco, grosor, velocidad, radios, anillo, sonando, fase,
      marcando, enMarcha]);

  const compruebaServidor = useCallback(() => {
    setServidorVivo(null);
    return fetch(`${SERVIDOR}/estado`, { cache: 'no-store' })
      .then((r) => r.json())
      .then(() => setServidorVivo(true))
      .catch(() => setServidorVivo(false));
  }, []);

  useEffect(() => { compruebaServidor(); }, [compruebaServidor]);

  // --- lienzos --------------------------------------------------------------
  // El trazo vive en una capa transparente propia y el vídeo se compone debajo
  // en cada fotograma, así la opacidad del vídeo es de verdad y a 0 desaparece.
  const dimensiona = useCallback((w, h) => {
    const c = dibujoRef.current;
    if (!c || !w || !h) return false;
    c.width = w; c.height = h;
    if (!trazoRef.current) trazoRef.current = document.createElement('canvas');
    trazoRef.current.width = w;
    trazoRef.current.height = h;
    const ctx = c.getContext('2d');
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, w, h);
    return true;
  }, []);

  const aseguraTamano = useCallback(() => {
    const c = dibujoRef.current;
    if (!c) return false;
    if (c.width > 0 && c.height > 0 && trazoRef.current) return true;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    return dimensiona(Math.floor((window.innerWidth || 1280) * dpr),
                      Math.floor((window.innerHeight || 720) * dpr));
  }, [dimensiona]);

  useEffect(() => {
    const ajusta = () => {
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      dimensiona(Math.floor(window.innerWidth * dpr), Math.floor(window.innerHeight * dpr));
    };
    ajusta();
    window.addEventListener('resize', ajusta);
    return () => window.removeEventListener('resize', ajusta);
  }, [dimensiona]);

  const limpia = useCallback(() => {
    const c = dibujoRef.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, c.width, c.height);
    if (trazoRef.current) {
      trazoRef.current.getContext('2d').clearRect(0, 0, c.width, c.height);
    }
    trazosRef.current = [];
  }, []);

  // --- sonido ---------------------------------------------------------------
  const arrancaAudio = useCallback(() => {
    if (audioRef.current) return;
    const Ctx = window.AudioContext || window.webkitAudioContext;
    const ctx = new Ctx();
    const filtro = ctx.createBiquadFilter();
    filtro.type = 'lowpass';
    filtro.frequency.value = 1600;
    const gan = ctx.createGain();
    gan.gain.value = volumen;
    filtro.connect(gan);
    gan.connect(ctx.destination);
    audioRef.current = ctx; filtroRef.current = filtro; ganRef.current = gan;
  }, [volumen]);

  const paraAudio = useCallback(() => {
    if (audioRef.current) { audioRef.current.close(); audioRef.current = null; }
    filtroRef.current = null; ganRef.current = null;
  }, []);

  const nota = useCallback((freq, dur, timbre) => {
    const ctx = audioRef.current;
    if (!ctx) return;
    const osc = ctx.createOscillator();
    const env = ctx.createGain();
    osc.type = timbre;
    osc.frequency.setValueAtTime(freq, ctx.currentTime);
    env.gain.setValueAtTime(0, ctx.currentTime);
    env.gain.linearRampToValueAtTime(0.9, ctx.currentTime + 0.006);
    env.gain.exponentialRampToValueAtTime(0.0008, ctx.currentTime + dur);
    osc.connect(env); env.connect(filtroRef.current);
    osc.start(); osc.stop(ctx.currentTime + dur + 0.02);
  }, []);

  useEffect(() => {
    if (ganRef.current && audioRef.current) {
      ganRef.current.gain.setTargetAtTime(volumen, audioRef.current.currentTime, 0.05);
    }
  }, [volumen]);

  const suena = useCallback((movimiento, radio, n) => {
    if (!cfg.current.sonando || !audioRef.current || !filtroRef.current) return;
    const ahora = performance.now();
    const espera = 250 - Math.min(195, movimiento * 13);
    if (ahora - ultimaNotaRef.current > espera && movimiento > 0.5) {
      ultimaNotaRef.current = ahora;
      const grado = ESCALA[Math.floor((radio / 17) % ESCALA.length)];
      const octava = 2 + Math.floor((radio / 115) % 3);
      nota(55 * Math.pow(2, octava + grado / 12), 0.1 + Math.min(0.45, movimiento / 38),
        n > 10 ? 'triangle' : n > 6 ? 'sine' : 'sawtooth');
    }
    filtroRef.current.frequency.setTargetAtTime(300 + radio * 12, audioRef.current.currentTime, 0.1);
  }, [nota]);

  // --- pintar un fotograma --------------------------------------------------
  // `pares` son los tramos [x0,y0,x1,y1] de cada punto entre el fotograma
  // anterior y este. Van emparejados por identidad del punto, no por su sitio
  // en la lista: si una pata se pierde un fotograma, las demás siguen pintando.
  const pinta = useCallback((ctx, pts, pares, CX, CY) => {
    const { color: COL, grosor: G, radios: R, anillo: A } = cfg.current;
    const [r, g, b] = hexARgb(COL);
    const rgba = (a) => `rgba(${r}, ${g}, ${b}, ${a})`;
    let movimiento = 0;

    if (pares && pares.length) {
      ctx.lineWidth = G;
      pares.forEach(([x0, y0, x1, y1]) => {
        const d = Math.hypot(x1 - x0, y1 - y0);
        movimiento += d;
        ctx.strokeStyle = rgba(0.16 + 0.7 * Math.min(1, d / 40));
        ctx.beginPath(); ctx.moveTo(x0, y0); ctx.lineTo(x1, y1); ctx.stroke();
        trazosRef.current.push([x0, y0, x1, y1]);
      });
      movimiento /= pares.length;
    }

    if (R) {
      ctx.strokeStyle = rgba(0.09);
      ctx.lineWidth = Math.max(0.5, G * 0.6);
      ctx.beginPath();
      pts.forEach((p) => { ctx.moveTo(CX, CY); ctx.lineTo(p[0], p[1]); });
      ctx.stroke();
    }
    if (A && pts.length > 2) {
      ctx.beginPath();
      pts.forEach((p, i) => (i ? ctx.lineTo(p[0], p[1]) : ctx.moveTo(p[0], p[1])));
      ctx.closePath();
      ctx.strokeStyle = rgba(Math.min(0.6, 0.1 + movimiento / 70));
      ctx.lineWidth = G;
      ctx.stroke();
    }
    ctx.fillStyle = rgba(0.9);
    pts.forEach((p) => ctx.fillRect(p[0] - G, p[1] - G, G * 2, G * 2));

    if (trazosRef.current.length > 120000) trazosRef.current.splice(0, 40000);
    return movimiento;
  }, []);

  // Dos formas de situar el trazo. Con el vídeo a la vista tiene que caer
  // sobre las patas, o sea en las coordenadas del vídeo y sin anclar. Con el
  // vídeo a 0 no hay nada con lo que cuadrar, así que se encuadra el propio
  // trazo para que llene la pantalla y se puede anclar al cuerpo.
  const encuadreSobreVideo = useCallback(() => {
    const cd = dibujoRef.current;
    const v = videoRef.current;
    const j = pistasRef.current;
    if (!cd || !v?.videoWidth || !j) return null;
    const esc = Math.min(cd.width / v.videoWidth, cd.height / v.videoHeight);
    const dw = v.videoWidth * esc, dh = v.videoHeight * esc;
    const ox = (cd.width - dw) / 2, oy = (cd.height - dh) / 2;
    const [bx0, by0, bx1, by1] = j.caja || [0, 0, 1, 1];
    return { minX: 0, minY: 0,
      kx: dw * (bx1 - bx0), ky: dh * (by1 - by0),
      ox: ox + dw * bx0, oy: oy + dh * by0 };
  }, []);

  // --- reproducir el seguimiento -------------------------------------------
  const pasoPistas = useCallback((ctx) => {
    const P = pistasRef.current;
    const enc = (cfg.current.opacidadVideo > 0.01 && encuadreSobreVideo()) || encuadreRef.current;
    if (!P || !enc) return;
    // Con el vídeo delante, el fotograma del trazo lo manda el reloj del vídeo:
    // si el trazo corriera por su cuenta, a los pocos segundos iría dibujando
    // patas de un momento distinto del que se está viendo.
    const v = videoRef.current;
    const fps = P.fps || 25;
    const alReloj = cfg.current.opacidadVideo > 0.01 && v && !v.srcObject
      && v.duration && Number.isFinite(v.currentTime)
      && Math.abs(cfg.current.velocidad - 1) < 0.01;
    if (alReloj) {
      const desde = tramoRef.current ? tramoRef.current[0] : 0;
      cuadroRef.current = Math.min(P.frames.length - 1,
        Math.max(0, Math.round((v.currentTime - desde) * fps)));
    } else {
      cuadroRef.current = (cuadroRef.current + Math.max(1, Math.round(cfg.current.velocidad))) % P.frames.length;
    }
    const i = cuadroRef.current;
    const f = P.frames[i];
    const ant = P.frames[i - 1] || null;
    const aX = (x) => enc.ox + (x - enc.minX) * enc.kx;
    const aY = (y) => enc.oy + (y - enc.minY) * enc.ky;

    const pts = [];
    const pares = [];
    f.coords.forEach((c, k) => {
      if (!f.visibles[k]) return;
      const x = aX(c[0]), y = aY(c[1]);
      pts.push([x, y]);
      if (ant && ant.visibles[k]) {
        pares.push([aX(ant.coords[k][0]), aY(ant.coords[k][1]), x, y]);
      }
    });
    if (!pts.length) return;
    const CX = aX(f.centroide[0]);
    const CY = aY(f.centroide[1]);
    const mov = pinta(ctx, pts, pares, CX, CY);
    const radio = pts.reduce((s, p) => s + Math.hypot(p[0] - CX, p[1] - CY), 0) / pts.length;
    suena(mov, radio, pts.length);
    setLectura({ movimiento: mov, radio, puntos: pts.length, cuadro: i, total: P.frames.length });
  }, [pinta, suena, encuadreSobreVideo]);

  // --- seguimiento en directo (webcam) -------------------------------------
  const pasoDirecto = useCallback((ctx, cd) => {
    const v = videoRef.current;
    const ca = analisisRef.current;
    if (!v || !ca || v.readyState < 2 || !v.videoWidth) return;
    const w = ANCHO_ANALISIS;
    const h = Math.max(1, Math.round((v.videoHeight / v.videoWidth) * w));
    if (ca.width !== w || ca.height !== h) { ca.width = w; ca.height = h; }
    const c2 = ca.getContext('2d', { willReadFrequently: true });
    c2.drawImage(v, 0, 0, w, h);
    const d = c2.getImageData(0, 0, w, h).data;

    const hist = new Uint32Array(256);
    for (let i = 0; i < d.length; i += 4) {
      hist[(d[i] * 0.299 + d[i + 1] * 0.587 + d[i + 2] * 0.114) | 0]++;
    }
    const meta = w * h * 0.025;
    let acc = 0, U = 0;
    for (let l = 0; l < 256; l++) { acc += hist[l]; if (acc >= meta) { U = l; break; } }

    let sx = 0, sy = 0, n = 0;
    const dentro = new Uint8Array(w * h);
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const i = (y * w + x) * 4;
        if (d[i] * 0.299 + d[i + 1] * 0.587 + d[i + 2] * 0.114 < U) {
          dentro[y * w + x] = 1; sx += x; sy += y; n++;
        }
      }
    }
    if (n < 12) return;
    const cx = sx / n, cy = sy / n;
    const N = 8;
    const mR = new Float32Array(N), mX = new Float32Array(N), mY = new Float32Array(N);
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        if (!dentro[y * w + x]) continue;
        const dx = x - cx, dy = y - cy;
        const rr = Math.hypot(dx, dy);
        let a = Math.atan2(dy, dx); if (a < 0) a += Math.PI * 2;
        const s = Math.min(N - 1, Math.floor((a / (Math.PI * 2)) * N));
        if (rr > mR[s]) { mR[s] = rr; mX[s] = x; mY[s] = y; }
      }
    }
    const esc = Math.min(cd.width / v.videoWidth, cd.height / v.videoHeight);
    const dw = v.videoWidth * esc, dh = v.videoHeight * esc;
    const ox = (cd.width - dw) / 2, oy = (cd.height - dh) / 2;
    const pts = [];
    for (let s = 0; s < N; s++) if (mR[s] > 2) pts.push([ox + (mX[s] / w) * dw, oy + (mY[s] / h) * dh]);
    if (!pts.length) return;
    const CX = ox + (cx / w) * dw, CY = oy + (cy / h) * dh;
    const ant = previasRef.current;
    const pares = ant && ant.length === pts.length
      ? pts.map((p, i) => [ant[i][0], ant[i][1], p[0], p[1]])
      : null;
    const mov = pinta(ctx, pts, pares, CX, CY);
    previasRef.current = pts;
    const radio = pts.reduce((s, p) => s + Math.hypot(p[0] - CX, p[1] - CY), 0) / pts.length;
    suena(mov, radio, pts.length);
    setLectura({ movimiento: mov, radio, puntos: pts.length, cuadro: 0, total: 0 });
  }, [pinta, suena]);

  // --- bucle ----------------------------------------------------------------
  const paso = useCallback(() => {
    ultimoTickRef.current = performance.now();
    const cd = dibujoRef.current;
    if (!cd || !aseguraTamano()) return;
    const { eco: E, fase: F, opacidadVideo: OV, marcando: M } = cfg.current;
    if (M) return;                       // mientras se marcan las patas, el lienzo es el fotograma

    // Con el vídeo cargado y aún sin seguimiento se ve el vídeo, para saber qué
    // se ha abierto y que el análisis no arranque a ciegas.
    const tramo = tramoRef.current;
    if (tramo) {
      const v = videoRef.current;
      if (v && v.duration && (v.currentTime < tramo[0] - 0.05 || v.currentTime >= tramo[1])) {
        try { v.currentTime = tramo[0]; } catch (err) { /* nada */ }
        if (cfg.current.enMarcha) v.play().catch(() => {});
      }
    }

    if (F === 'listo' || F === 'analizando') {
      const c = cd.getContext('2d');
      c.fillStyle = '#000';
      c.fillRect(0, 0, cd.width, cd.height);
      const vv = videoRef.current;
      if (vv && vv.videoWidth) {
        const e = Math.min(cd.width / vv.videoWidth, cd.height / vv.videoHeight);
        const dw = vv.videoWidth * e, dh = vv.videoHeight * e;
        c.drawImage(vv, (cd.width - dw) / 2, (cd.height - dh) / 2, dw, dh);
      }
      return;
    }
    if (F !== 'siguiendo' && F !== 'directo') return;

    // 1 · el trazo se acumula en su capa, y el eco lo va borrando
    const tctx = trazoRef.current.getContext('2d');
    if (E > 0) {
      tctx.globalCompositeOperation = 'destination-out';
      tctx.fillStyle = `rgba(0,0,0,${E})`;
      tctx.fillRect(0, 0, cd.width, cd.height);
      tctx.globalCompositeOperation = 'source-over';
    }
    if (cfg.current.enMarcha) {
      if (F === 'siguiendo') pasoPistas(tctx); else pasoDirecto(tctx, cd);
    }

    // 2 · se compone: negro, el vídeo con su opacidad, y encima el trazo
    const ctx = cd.getContext('2d');
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, cd.width, cd.height);
    const v = videoRef.current;
    if (OV > 0.01 && v && v.videoWidth) {
      const esc = Math.min(cd.width / v.videoWidth, cd.height / v.videoHeight);
      const dw = v.videoWidth * esc, dh = v.videoHeight * esc;
      ctx.globalAlpha = OV;
      ctx.drawImage(v, (cd.width - dw) / 2, (cd.height - dh) / 2, dw, dh);
      ctx.globalAlpha = 1;
    }
    ctx.drawImage(trazoRef.current, 0, 0);
  }, [aseguraTamano, pasoPistas, pasoDirecto]);

  useEffect(() => {
    const conRaf = () => { rafRef.current = requestAnimationFrame(conRaf); paso(); };
    rafRef.current = requestAnimationFrame(conRaf);
    const respaldo = setInterval(() => {
      if (performance.now() - ultimoTickRef.current > 200) paso();
    }, 40);
    return () => { cancelAnimationFrame(rafRef.current); clearInterval(respaldo); };
  }, [paso]);

  // --- fuentes --------------------------------------------------------------
  const paraWebcam = () => {
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
  };

  const abreVideo = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    paraWebcam();
    ficheroRef.current = f;
    pistasRef.current = null;
    tramoRef.current = null;
    const v = videoRef.current;
    v.srcObject = null;
    v.src = URL.createObjectURL(f);
    v.loop = true; v.muted = true;
    v.onloadeddata = () => { v.currentTime = 0; };
    v.onerror = () => setAviso(
      `${f.name} no se puede reproducir aquí. Chrome abre mp4/H.264, webm y mov con H.264; `
      + 'si viene de una cámara o del móvil, pásalo a mp4 antes.');
    v.play().catch(() => {});
    setFuente('video'); setNombre(f.name); setFase('listo'); setMarcas([]); setMarcando(false);
    limpia();
    setAviso(`${f.name} · ahora «Seguir las patas»`);
  };

  const abreWebcam = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { width: 1280, height: 720 }, audio: false });
      streamRef.current = stream;
      pistasRef.current = null;
      ficheroRef.current = null;
      tramoRef.current = null;
      const v = videoRef.current;
      v.removeAttribute('src'); v.srcObject = stream; v.muted = true;
      await v.play();
      setFuente('webcam'); setNombre('webcam'); setFase('directo'); setMarcando(false);
      limpia();
      setAviso('webcam en directo · el seguimiento se calcula aquí, por umbral');
    } catch (err) {
      setAviso(`sin webcam: ${err.message}`);
    }
  };

  // Anclar al cuerpo resta el centroide de cada fotograma. Con la cámara a mano
  // el encuadre se mueve mucho más que las patas y se come el dibujo; anclando
  // queda el movimiento de las patas respecto a la araña.
  const preparaPistas = useCallback((crudo, anclar) => {
    const j = anclar
      ? { ...crudo, frames: crudo.frames.map((f) => ({
          ...f,
          coords: f.coords.map((c) => [c[0] - f.centroide[0] + 0.5, c[1] - f.centroide[1] + 0.5]),
          centroide: [0.5, 0.5],
        })) }
      : crudo;
    aseguraTamano();
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    j.frames.forEach((fr) => fr.coords.forEach((c, k) => {
      if (!fr.visibles[k]) return;
      minX = Math.min(minX, c[0]); maxX = Math.max(maxX, c[0]);
      minY = Math.min(minY, c[1]); maxY = Math.max(maxY, c[1]);
    }));
    const cd = dibujoRef.current;
    const m = 0.08;
    const k = Math.min(cd.width * (1 - m * 2) / Math.max(1e-6, maxX - minX),
                       cd.height * (1 - m * 2) / Math.max(1e-6, maxY - minY));
    encuadreRef.current = { minX, minY, kx: k, ky: k,
      ox: (cd.width - (maxX - minX) * k) / 2, oy: (cd.height - (maxY - minY) * k) / 2 };
    pistasRef.current = j;
    cuadroRef.current = 0;
    // El seguimiento cubre un trozo del vídeo, no el vídeo entero. Si se deja
    // correr todo, el trazo acaba dibujado encima de otro plano y parece que
    // el tracking falla cuando lo que falla es lo que se está viendo.
    tramoRef.current = (typeof j.desde_s === 'number' && typeof j.hasta_s === 'number'
      && j.hasta_s > j.desde_s) ? [j.desde_s, j.hasta_s] : null;
    const v = videoRef.current;
    if (tramoRef.current && v) {
      v.loop = false;
      try { v.currentTime = tramoRef.current[0]; } catch (err) { /* aún sin metadatos */ }
    }
    limpia();
  }, [aseguraTamano, limpia]);

  const aplicaPistas = useCallback((j, etiqueta) => {
    if (!j.frames?.length) throw new Error('el seguimiento viene vacío');
    crudoRef.current = j;
    preparaPistas(j, anclado && opacidadVideo <= 0.01);
    setMarcando(false);
    setFase('siguiendo');
    setAviso(etiqueta);
  }, [preparaPistas, anclado, opacidadVideo]);

  const videoALaVista = opacidadVideo > 0.01;
  useEffect(() => {
    if (crudoRef.current) preparaPistas(crudoRef.current, anclado && !videoALaVista);
  }, [anclado, videoALaVista, preparaPistas]);

  const analiza = async (modo) => {
    const f = ficheroRef.current;
    if (!f) return;
    if (modo === 'manual' && marcas.length < 3) {
      setAviso('para el modo manual marca al menos tres patas: dale a «Marcar patas»');
      return;
    }
    const v = videoRef.current;
    const segundo = v ? v.currentTime : 0;
    setMarcando(false);
    setFase('analizando');
    setProgreso({ pct: 0, mensaje: 'subiendo el vídeo', queda: null });
    setAviso(modo === 'manual'
      ? `${marcas.length} patas marcadas en el segundo ${segundo.toFixed(2)}, propagando hacia delante y hacia atrás`
      : 'SAM 3 busca las patas y CoTracker las sigue por todo el vídeo');
    try {
      const q = new URLSearchParams({ nombre: f.name, puntos: '8', salto: '2', max: '1200', ancho: '384' });
      const cab = { 'Content-Type': 'application/octet-stream' };
      if (modo === 'manual') {
        cab['X-Patas'] = JSON.stringify({ t: +segundo.toFixed(3), puntos: marcas });
      }
      const r = await fetch(`${SERVIDOR}/analizar?${q}`, { method: 'POST', body: f, headers: cab });
      const abierto = await r.json();
      if (!r.ok || !abierto.trabajo) throw new Error(abierto.error || 'no arrancó el análisis');
      idTrabajoRef.current = abierto.trabajo;

      // El trabajo corre en el servidor y se le va preguntando por él.
      const j = await new Promise((resuelve, rechaza) => {
        trabajoRef.current = setInterval(async () => {
          try {
            const p = await fetch(`${SERVIDOR}/trabajo?id=${abierto.trabajo}`, { cache: 'no-store' });
            const e = await p.json();
            if (e.estado === 'listo') { clearInterval(trabajoRef.current); resuelve(e.resultado); }
            else if (e.estado === 'cancelado') { clearInterval(trabajoRef.current); rechaza(new Error('cancelado')); }
            else if (e.estado === 'falló') { clearInterval(trabajoRef.current); rechaza(new Error(e.detalle)); }
            else setProgreso({ pct: e.pct || 0, mensaje: e.mensaje || '', queda: e.queda });
          } catch (err) {
            clearInterval(trabajoRef.current);
            rechaza(new Error('se perdió el servidor'));
          }
        }, 1000);
      });
      const donde = j.guardado ? ` · guardado en ${j.guardado.split('/').slice(-2).join('/')}` : '';
      aplicaPistas(j, j.aviso
        ? `⚠ ${j.aviso}`
        : `${j.video} · ${j.frames.length} fotogramas · ${j.puntos.length} patas`
          + (tramoRef.current ? ` · del segundo ${j.desde_s.toFixed(1)} al ${j.hasta_s.toFixed(1)}` : '')
          + donde);
    } catch (err) {
      setFase('listo');
      setAviso(err.message === 'cancelado' ? 'análisis cancelado' : `no se pudo analizar: ${err.message}`);
    } finally {
      idTrabajoRef.current = null;
    }
  };

  const cancela = async () => {
    const id = idTrabajoRef.current;
    clearInterval(trabajoRef.current);
    setFase('listo');
    setAviso('cancelando…');
    try {
      await fetch(`${SERVIDOR}/cancelar?id=${id}`, { method: 'POST' });
      setAviso('análisis cancelado');
    } catch (err) {
      setAviso(`no se pudo cancelar: ${err.message}`);
    }
    idTrabajoRef.current = null;
  };

  useEffect(() => () => clearInterval(trabajoRef.current), []);

  // El clip que viene con la app: sus patas están seguidas fotograma a
  // fotograma con SAM 3 Video, no arrastrando puntos, y el trazo va con él.
  const cargaEjemplo = useCallback(async () => {
    try {
      const r = await fetch('/spider/arana.json', { cache: 'no-store' });
      const j = await r.json();
      paraWebcam();
      const v = videoRef.current;
      v.srcObject = null;
      v.src = '/spider/arana.mp4';
      v.loop = true; v.muted = true;
      v.play().catch(() => {});
      ficheroRef.current = null;
      setFuente('ejemplo'); setNombre('arana.mp4');
      aplicaPistas(j, `arana.mp4 · ${j.puntos.length} patas seguidas fotograma a fotograma`);
    } catch (err) {
      setAviso(`no se pudo cargar el ejemplo: ${err.message}`);
    }
  }, [aplicaPistas]);

  // Al abrir la pestaña ya hay algo que mirar, sin cargar nada a mano.
  const arrancado = useRef(false);
  useEffect(() => {
    if (arrancado.current) return;
    arrancado.current = true;
    cargaEjemplo();
  }, [cargaEjemplo]);

  // Un trazo no se abre solo: se aplica sobre el vídeo que ya está cargado, que
  // es de donde salió. Por eso el botón solo aparece con un vídeo abierto.
  const cargaJson = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const l = new FileReader();
    l.onload = async () => {
      try {
        const j = JSON.parse(l.result);
        // El trazo sabe de qué vídeo salió, pero el navegador no puede abrir un
        // fichero del disco por su ruta. Se le pide al servidor, que sí sabe
        // dónde está.
        if (j.video && j.video !== nombre) {
          const url = `${SERVIDOR}/video?nombre=${encodeURIComponent(j.video)}`;
          const hay = await fetch(url, { method: 'HEAD' }).then((r) => r.ok).catch(() => false);
          if (hay) {
            paraWebcam();
            const v = videoRef.current;
            v.srcObject = null;
            v.src = url;
            v.loop = true; v.muted = true;
            v.play().catch(() => {});
            ficheroRef.current = null;
            setFuente('json'); setNombre(j.video);
          }
        }
        aplicaPistas(j, `${j.video || f.name} · ${j.puntos.length} patas del trazo guardado`);
      } catch (err) { setAviso(`no se pudo leer: ${err.message}`); }
    };
    l.readAsText(f);
  };

  useEffect(() => () => { paraWebcam(); paraAudio(); }, [paraAudio]);

  // --- marcar las patas a mano ---------------------------------------------
  const clicLienzo = (e) => {
    if (!marcando) return;
    const v = videoRef.current;
    const cd = dibujoRef.current;
    if (!v?.videoWidth) return;
    const rect = cd.getBoundingClientRect();
    const dpr = cd.width / rect.width;
    const px = (e.clientX - rect.left) * dpr;
    const py = (e.clientY - rect.top) * dpr;
    const esc = Math.min(cd.width / v.videoWidth, cd.height / v.videoHeight);
    const dw = v.videoWidth * esc, dh = v.videoHeight * esc;
    const ox = (cd.width - dw) / 2, oy = (cd.height - dh) / 2;
    const x = (px - ox) / dw, y = (py - oy) / dh;
    if (x < 0 || x > 1 || y < 0 || y > 1) return;
    setMarcas((m) => [...m, [+x.toFixed(4), +y.toFixed(4)]]);
  };

  useEffect(() => {
    if (!marcando) return;
    const cd = dibujoRef.current;
    const v = videoRef.current;
    if (!cd || !v?.videoWidth) return;
    const ctx = cd.getContext('2d');
    ctx.fillStyle = '#000'; ctx.fillRect(0, 0, cd.width, cd.height);
    const esc = Math.min(cd.width / v.videoWidth, cd.height / v.videoHeight);
    const dw = v.videoWidth * esc, dh = v.videoHeight * esc;
    const ox = (cd.width - dw) / 2, oy = (cd.height - dh) / 2;
    ctx.drawImage(v, ox, oy, dw, dh);
    const [r, g, b] = hexARgb(color);
    marcas.forEach(([x, y], i) => {
      const px = ox + x * dw, py = oy + y * dh;
      ctx.strokeStyle = `rgba(${r},${g},${b},0.95)`;
      ctx.lineWidth = 2;
      ctx.beginPath(); ctx.arc(px, py, 9, 0, Math.PI * 2); ctx.stroke();
      ctx.fillStyle = `rgba(${r},${g},${b},0.95)`;
      ctx.font = '13px system-ui';
      ctx.fillText(String(i + 1), px + 12, py + 4);
    });
  }, [marcando, marcas, color, tiempo.t]);

  // Fotograma a fotograma. El vídeo HTML no da el número de fotograma, así que
  // se salta por tiempo; 25 fps es el paso por defecto y basta para encuadrar.
  const PASO = 1 / 25;

  const saltaA = useCallback((segundo) => {
    const v = videoRef.current;
    if (!v || !v.duration) return;
    v.pause();
    v.currentTime = Math.max(0, Math.min(v.duration - 1e-3, segundo));
    setMarcas([]);
  }, []);

  const mueve = useCallback((cuantos) => {
    const v = videoRef.current;
    if (v) saltaA(v.currentTime + cuantos * PASO);
  }, [saltaA, PASO]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const apunta = () => setTiempo({ t: v.currentTime, dur: v.duration || 0 });
    v.addEventListener('timeupdate', apunta);
    v.addEventListener('seeked', apunta);
    v.addEventListener('loadedmetadata', apunta);
    return () => {
      v.removeEventListener('timeupdate', apunta);
      v.removeEventListener('seeked', apunta);
      v.removeEventListener('loadedmetadata', apunta);
    };
  }, []);

  useEffect(() => {
    if (!marcando) return;
    const tecla = (e) => {
      if (e.key === 'ArrowLeft') { e.preventDefault(); mueve(-1); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); mueve(1); }
    };
    window.addEventListener('keydown', tecla);
    return () => window.removeEventListener('keydown', tecla);
  }, [marcando, mueve]);

  // El mismo botón para el vídeo y para el trazo: en pausa se congelan los dos.
  const marchaOPausa = useCallback(() => {
    setEnMarcha((antes) => {
      const ahora = !antes;
      const v = videoRef.current;
      if (v && (v.src || v.srcObject)) {
        if (ahora) v.play().catch(() => {}); else v.pause();
      }
      return ahora;
    });
  }, []);

  useEffect(() => {
    const tecla = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.code === 'Space') { e.preventDefault(); marchaOPausa(); }
      else if (e.key === 'p' || e.key === 'P') setPlegado((x) => !x);
    };
    window.addEventListener('keydown', tecla);
    return () => window.removeEventListener('keydown', tecla);
  }, [marchaOPausa]);

  const empiezaAMarcar = () => {
    const v = videoRef.current;
    if (v) v.pause();
    setEnMarcha(false);
    setMarcando(true);
    setAviso('busca el fotograma con las flechas ← → y haz clic en la punta de cada pata');
  };

  const dejaDeMarcar = () => {
    setMarcando(false);
    const v = videoRef.current;
    if (v && fuente === 'video') v.play().catch(() => {});
    limpia();
    setAviso(marcas.length >= 3
      ? `${marcas.length} patas marcadas · ahora «Seguir las patas»`
      : `${nombre} · ahora «Seguir las patas»`);
  };

  // --- exportar -------------------------------------------------------------
  const guardaPNG = () => {
    const a = document.createElement('a');
    a.download = `spidertrack-${Date.now()}.png`;
    a.href = dibujoRef.current.toDataURL('image/png');
    a.click();
  };

  const guardaSVG = () => {
    const c = dibujoRef.current;
    const [r, g, b] = hexARgb(color);
    const lineas = trazosRef.current.map(([x0, y0, x1, y1]) =>
      `<line x1="${x0.toFixed(1)}" y1="${y0.toFixed(1)}" x2="${x1.toFixed(1)}" y2="${y1.toFixed(1)}"/>`).join('');
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${c.width}" height="${c.height}" `
      + `viewBox="0 0 ${c.width} ${c.height}"><rect width="100%" height="100%" fill="#000"/>`
      + `<g stroke="rgb(${r},${g},${b})" stroke-width="${grosor}" fill="none" stroke-opacity="0.55">`
      + `${lineas}</g></svg>`;
    const a = document.createElement('a');
    a.download = `spidertrack-${Date.now()}.svg`;
    a.href = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }));
    a.click();
  };

  const guardaJSON = () => {
    if (!pistasRef.current) return;
    const a = document.createElement('a');
    a.download = `${(nombre || 'seguimiento').replace(/\.[^.]+$/, '')}-seguimiento.json`;
    a.href = URL.createObjectURL(new Blob([JSON.stringify(pistasRef.current)], { type: 'application/json' }));
    a.click();
  };

  // --- estilos --------------------------------------------------------------
  const A = 'rgba(200, 180, 150, ';
  const st = {
    raiz: { position: 'fixed', inset: 0, background: '#000', overflow: 'hidden',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' },
    lienzo: { position: 'absolute', inset: 0, width: '100%', height: '100%', display: 'block',
      cursor: marcando ? 'crosshair' : 'default' },
    panel: { position: 'absolute', top: '20px', left: '20px', width: '272px',
      maxHeight: 'calc(100vh - 110px)', overflowY: 'auto', padding: '16px',
      background: 'rgba(10,10,10,0.93)', border: `1px solid ${A}0.2)`, borderRadius: '10px',
      backdropFilter: 'blur(14px)', zIndex: 10 },
    titulo: { fontSize: '11px', fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase',
      color: `${A}0.85)`, marginBottom: '4px' },
    aviso: { fontSize: '9px', lineHeight: 1.5, color: `${A}0.45)`, marginBottom: '14px', wordBreak: 'break-word' },
    paso: { fontSize: '9px', letterSpacing: '0.1em', textTransform: 'uppercase', color: `${A}0.4)`,
      margin: '16px 0 7px' },
    fila: { display: 'flex', gap: '6px', marginBottom: '8px' },
    boton: { flex: 1, padding: '8px 4px', background: 'transparent', border: `1px solid ${A}0.28)`,
      borderRadius: '4px', color: `${A}0.8)`, fontSize: '9px', fontWeight: 600, letterSpacing: '0.06em',
      textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'inherit' },
    activo: { background: `${A}0.16)`, borderColor: `${A}0.5)` },
    apagado: { opacity: 0.35, cursor: 'default' },
    etiqueta: { display: 'flex', justifyContent: 'space-between', fontSize: '9px', letterSpacing: '0.06em',
      textTransform: 'uppercase', color: `${A}0.55)`, marginBottom: '4px', marginTop: '10px' },
    rango: { width: '100%' },
    dato: { display: 'flex', justifyContent: 'space-between', fontSize: '9px', color: `${A}0.5)`, padding: '2px 0' },
    valor: { color: `${A}0.85)`, fontVariantNumeric: 'tabular-nums' },
    salir: { position: 'absolute', top: '20px', right: '20px', padding: '9px 16px',
      background: 'rgba(10,10,10,0.9)', border: `1px solid ${A}0.3)`, borderRadius: '6px',
      color: `${A}0.85)`, fontSize: '10px', fontWeight: 600, letterSpacing: '0.1em',
      textTransform: 'uppercase', cursor: 'pointer', zIndex: 10, fontFamily: 'inherit' },
    file: { display: 'none' },
    barra: { height: '4px', background: `${A}0.12)`, borderRadius: '2px', overflow: 'hidden' },
    mandos: { position: 'absolute', bottom: '20px', left: '20px', width: '272px',
      display: 'flex', gap: '6px',
      zIndex: 11 },
    mando: { padding: '10px 14px', minWidth: '46px', background: 'rgba(10,10,10,0.92)',
      border: `1px solid ${A}0.3)`, borderRadius: '6px', color: `${A}0.85)`, fontSize: '12px',
      letterSpacing: '0.06em', cursor: 'pointer', fontFamily: 'inherit',
      backdropFilter: 'blur(14px)' },
    relleno: { height: '100%', background: `${A}0.7)`, transition: 'width 0.4s linear' },
  };

  const hayPistas = fase === 'siguiendo';

  return (
    <div style={st.raiz}>
      <canvas ref={dibujoRef} style={st.lienzo} onClick={clicLienzo} />
      <video ref={videoRef} playsInline muted style={{ display: 'none' }} />
      <canvas ref={analisisRef} style={{ display: 'none' }} />

      {!plegado && (
      <div style={st.panel}>
        <div style={st.titulo}>SpiderTrack</div>
        <div style={st.aviso}>{aviso}</div>

        <div style={st.fila}>
          <button style={{ ...st.boton, ...(pestana === 'dibujo' ? st.activo : {}) }}
                  onClick={() => setPestana('dibujo')}>Dibujo</button>
          <button style={{ ...st.boton, ...(pestana === 'sonido' ? st.activo : {}) }}
                  onClick={() => setPestana('sonido')}>Sonido</button>
        </div>

        {pestana === 'dibujo' && (
          <>
            <div style={st.paso}>1 · La araña</div>
            <div style={st.fila}>
              <label style={{ ...st.boton, ...(fuente === 'video' ? st.activo : {}), textAlign: 'center' }}>
                Vídeo
                <input type="file" accept="video/*" onChange={abreVideo} style={st.file} />
              </label>
              <button style={{ ...st.boton, ...(fuente === 'webcam' ? st.activo : {}) }} onClick={abreWebcam}>
                Webcam
              </button>
            </div>
            <div style={st.fila}>
              <button style={{ ...st.boton, ...(fuente === 'ejemplo' ? st.activo : {}) }} onClick={cargaEjemplo}>
                Ejemplo
              </button>
              <label style={{ ...st.boton, ...(fuente === 'json' ? st.activo : {}), textAlign: 'center' }}>
                Abrir trazo
                <input type="file" accept="application/json,.json" onChange={cargaJson} style={st.file} />
              </label>
            </div>

            {fuente === 'video' && (
              <>
                <div style={st.paso}>2 · Seguir las patas</div>
                {servidorVivo === false && (
                  <>
                    <div style={{ ...st.aviso, marginBottom: '8px' }}>
                      No contesta el servidor de seguimiento. Debería arrancar solo al encender el
                      Mac; si no, en una terminal:{' '}
                      <code>spidertrack/.venv/bin/python spidertrack/servidor.py</code>. En Safari
                      la llamada al servidor local queda bloqueada, así que esta parte pide Chrome.
                    </div>
                    <div style={st.fila}>
                      <button style={st.boton} onClick={compruebaServidor}>Reintentar</button>
                    </div>
                  </>
                )}
                <div style={st.fila}>
                  <button
                    style={{ ...st.boton, ...(fase === 'analizando' || servidorVivo === false ? st.apagado : {}) }}
                    onClick={() => analiza('auto')}
                    disabled={fase === 'analizando' || servidorVivo === false}
                  >
                    Auto
                  </button>
                  <button
                    style={{ ...st.boton,
                      ...(fase === 'analizando' || servidorVivo === false || marcas.length < 3 ? st.apagado : {}) }}
                    onClick={() => analiza('manual')}
                    disabled={fase === 'analizando' || servidorVivo === false || marcas.length < 3}
                  >
                    Manual{marcas.length >= 3 ? ` (${marcas.length})` : ''}
                  </button>
                </div>

                {fase === 'analizando' && (
                  <div style={{ marginBottom: '10px' }}>
                    <div style={st.barra}>
                      <div style={{ ...st.relleno, width: `${Math.max(2, progreso.pct)}%` }} />
                    </div>
                    <div style={{ ...st.dato, marginTop: '5px' }}>
                      <span>{progreso.mensaje || 'preparando'}</span>
                      <span style={st.valor}>{progreso.pct}%</span>
                    </div>
                    <div style={{ ...st.fila, marginTop: '8px' }}>
                      <button style={st.boton} onClick={cancela}>Cancelar</button>
                    </div>
                    {progreso.queda != null && (
                      <div style={st.dato}>
                        <span>Queda</span>
                        <span style={st.valor}>
                          {progreso.queda === 0
                            ? 'acabando'
                            : progreso.queda > 90
                              ? `~${Math.ceil(progreso.queda / 60)} min`
                              : `~${progreso.queda} s`}
                        </span>
                      </div>
                    )}
                  </div>
                )}
                <div style={st.fila}>
                  <button style={{ ...st.boton, ...(marcando ? st.activo : {}) }}
                          onClick={marcando ? dejaDeMarcar : empiezaAMarcar}>
                    {marcando ? `Listo (${marcas.length})` : marcas.length ? `Patas: ${marcas.length}` : 'Marcar patas'}
                  </button>
                  {marcas.length > 0 && (
                    <button style={st.boton} onClick={() => setMarcas([])}>Borrar</button>
                  )}
                </div>
                {marcando && (
                  <>
                    <div style={st.fila}>
                      <button style={st.boton} onClick={() => mueve(-10)}>««</button>
                      <button style={st.boton} onClick={() => mueve(-1)}>«</button>
                      <button style={st.boton} onClick={() => mueve(1)}>»</button>
                      <button style={st.boton} onClick={() => mueve(10)}>»»</button>
                    </div>
                    <input type="range" min="0" max={tiempo.dur || 0} step="0.001"
                           value={tiempo.t}
                           onChange={(e) => saltaA(+e.target.value)} style={st.rango} />
                    <div style={st.dato}>
                      <span>Fotograma</span>
                      <span style={st.valor}>
                        {Math.round(tiempo.t * 25)} / {Math.round((tiempo.dur || 0) * 25)}
                      </span>
                    </div>
                  </>
                )}

                <div style={st.aviso}>
                  <b>Auto</b>: SAM 3 busca la araña y pone los puntos en las puntas de las patas.
                  <b> Manual</b>: parte de las patas que marques tú, en el fotograma que fijes, y
                  propaga hacia delante y hacia atrás. Las flechas ← → mueven un fotograma.
                </div>

              </>
            )}

            <div style={st.paso}>3 · El trazo</div>
            <div style={{ ...st.etiqueta, marginTop: 0 }}><span>Color</span><span>{color}</span></div>
            <input type="color" value={color} onChange={(e) => setColor(e.target.value)}
                   style={{ width: '100%', height: '26px', background: 'transparent', border: 'none' }} />

            <div style={st.etiqueta}><span>Opacidad del vídeo</span><span>{Math.round(opacidadVideo * 100)}%</span></div>
            <input type="range" min="0" max="1" step="0.01" value={opacidadVideo}
                   onChange={(e) => setOpacidadVideo(+e.target.value)} style={st.rango} />

            <div style={st.etiqueta}><span>Eco</span><span>{eco === 0 ? 'sin borrar' : eco.toFixed(3)}</span></div>
            <input type="range" min="0" max="0.2" step="0.002" value={eco}
                   onChange={(e) => setEco(+e.target.value)} style={st.rango} />

            <div style={st.etiqueta}><span>Grosor</span><span>{grosor}</span></div>
            <input type="range" min="0.5" max="4" step="0.5" value={grosor}
                   onChange={(e) => setGrosor(+e.target.value)} style={st.rango} />

            <div style={st.etiqueta}><span>Velocidad</span><span>{velocidad}×</span></div>
            <input type="range" min="1" max="6" step="1" value={velocidad}
                   onChange={(e) => setVelocidad(+e.target.value)} style={st.rango} />

            <div style={{ ...st.fila, marginTop: '10px' }}>
              <button style={{ ...st.boton, ...(anclado && !videoALaVista ? st.activo : {}),
                        ...(videoALaVista ? st.apagado : {}) }}
                      title={videoALaVista ? 'Con el vídeo a la vista el trazo va sobre sus patas' : ''}
                      onClick={() => setAnclado(!anclado)}
                      disabled={videoALaVista}>Anclar al cuerpo</button>
              <button style={st.boton} onClick={limpia}>Limpiar</button>
            </div>
            <div style={st.fila}>
              <button style={{ ...st.boton, ...(radios ? st.activo : {}) }} onClick={() => setRadios(!radios)}>Radios</button>
              <button style={{ ...st.boton, ...(anillo ? st.activo : {}) }} onClick={() => setAnillo(!anillo)}>Anillo</button>
            </div>

            <div style={st.paso}>4 · Exportar</div>
            <div style={st.fila}>
              <button style={st.boton} onClick={guardaPNG}>PNG</button>
              <button style={st.boton} onClick={guardaSVG}>SVG</button>
              <button style={{ ...st.boton, ...(hayPistas ? {} : st.apagado) }} onClick={guardaJSON}>JSON</button>
            </div>
            <div style={st.aviso}>
              El SVG lleva el trazo como líneas, listo para llevarlo a un modelador. El JSON lleva
              las coordenadas de cada pata en cada fotograma.
            </div>

            <div style={{ paddingTop: '10px', borderTop: `1px solid ${A}0.12)` }}>
              <div style={st.dato}><span>Movimiento</span><span style={st.valor}>{lectura.movimiento.toFixed(2)}</span></div>
              <div style={st.dato}><span>Radio medio</span><span style={st.valor}>{lectura.radio.toFixed(0)}</span></div>
              <div style={st.dato}><span>Patas</span><span style={st.valor}>{lectura.puntos}</span></div>
              {lectura.total > 0 && (
                <div style={st.dato}><span>Fotograma</span>
                  <span style={st.valor}>{lectura.cuadro} / {lectura.total}</span></div>
              )}
            </div>
          </>
        )}

        {pestana === 'sonido' && (
          <>
            <div style={st.aviso}>
              El movimiento dispara la nota y marca el tempo, el radio medio elige la altura en una
              pentatónica, el número de patas cambia el timbre y la apertura del cuerpo mueve el
              filtro paso bajo.
            </div>
            <div style={st.fila}>
              <button style={{ ...st.boton, ...(sonando ? st.activo : {}) }}
                      onClick={() => {
                        if (sonando) { setSonando(false); paraAudio(); }
                        else { arrancaAudio(); setSonando(true); }
                      }}>
                {sonando ? 'Parar' : 'Sonar'}
              </button>
            </div>
            <div style={st.etiqueta}><span>Volumen</span><span>{Math.round(volumen * 100)}%</span></div>
            <input type="range" min="0" max="1" step="0.01" value={volumen}
                   onChange={(e) => setVolumen(+e.target.value)} style={st.rango} />
          </>
        )}
      </div>

      )}

      <div style={st.mandos}>
        <button style={st.mando} onClick={marchaOPausa}
                title="Espacio: marcha o pausa del vídeo y del trazo">
          {enMarcha ? '❙❙' : '▶'}
        </button>
        <button style={st.mando} onClick={() => setPlegado(!plegado)}
                title="P: plegar o desplegar el panel">
          {plegado ? '›  Panel' : '‹  Ocultar'}
        </button>
      </div>

      {onSalir && <button style={st.salir} onClick={onSalir}>← Volver</button>}
    </div>
  );
};

export default SpiderTrack;
